import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Constants } from "@constants";
import {
    BitacoraDTO,
    CuotaDTO,
    EncuestaCheckpointDTO,
    EncuestaDTO,
    EnvioDTO,
    FunnelDTO,
    GrupoDTO,
    GrupoHeadDTO,
    IBitacoraDTO,
    ICuotaDTO,
    IEncuestaCheckpointDTO,
    IEncuestaDTO,
    IEnvioDTO,
    IFunnelDTO,
    IGrupoDTO,
    IInvestigacionDTO,
    IMarcadorDTO,
    InvestigacionDTO,
    IPaginadoDTO,
    IRespuestaDiaDTO,
    IRespuestaEncuesta,
    MarcadorDTO,
    PaginadoDTO,
    RespuestaDiaDTO,
    RespuestaEncuesta
} from "@DTOS";
import { firstValueFrom, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { Nilable } from "@shared/utils";

@Injectable({
    providedIn: "root"
})
export class InvestigacionesServiceBackend {
    constructor(protected httpClient: HttpClient) { }

    public async getInvestigaciones(
        pagina: number, cantidad: number, activas: boolean
    ): Promise<PaginadoDTO<InvestigacionDTO>> {
        const encuestasPaginados = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<IInvestigacionDTO>>(
                `${Constants.apiURL}/researches?page=${pagina}&size=${cantidad}&show=${activas ? "active" : "all"}`
            )
        );

        const investigaciones = encuestasPaginados.results.map(investigacion => new InvestigacionDTO(investigacion));
        return new PaginadoDTO({ ...encuestasPaginados, results: investigaciones });
    }

    public async getInvestigacionesQuery(
        query: string, pagina: number, cantidad: number, activas: boolean
    ): Promise<PaginadoDTO<InvestigacionDTO>> {
        const encuestasPaginados = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<IInvestigacionDTO>>(
                `${Constants.apiURL}/researches`,
                {
                    params: {
                        page: pagina.toString(),
                        size: cantidad.toString(),
                        q: query,
                        show: activas ? "active" : "all"
                    }
                }
            )
        );

        const investigaciones = encuestasPaginados.results.map(investigacion => new InvestigacionDTO(investigacion));
        return new PaginadoDTO({ ...encuestasPaginados, results: investigaciones });
    }

    public async getInvestigacion(id: number): Promise<Nilable<InvestigacionDTO>> {
        return await firstValueFrom(
            this.httpClient.get<IInvestigacionDTO>(
                `${Constants.apiURL}/researches/${id}`
            ).pipe(
                catchError(() => of(null)),
                map(json => new InvestigacionDTO(json))
            )
        );
    }

    public async postInvestigacion(investigacion: InvestigacionDTO): Promise<InvestigacionDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IInvestigacionDTO>(
                `${Constants.apiURL}/researches`, investigacion.toJSON()
            )
        );

        return new InvestigacionDTO(response);
    }

    public async patchInvestigacion(investigacion: InvestigacionDTO): Promise<InvestigacionDTO> {
        const response = await firstValueFrom(
            this.httpClient.patch<IInvestigacionDTO>(
                `${Constants.apiURL}/researches/${investigacion.id}`, investigacion.toJSON()
            )
        );

        return new InvestigacionDTO(response);
    }

    public async getInvestigacionEncuestas(investigacion: InvestigacionDTO): Promise<EncuestaDTO[]> {
        const response = await firstValueFrom(
            this.httpClient.get<IEncuestaDTO[]>(
                `${Constants.apiURL}/researches/${investigacion.id}/combos`
            )
        );

        return response.map(encuesta => new EncuestaDTO(encuesta));
    }

    public async getInvestigacionGrupos(investigacion: InvestigacionDTO): Promise<GrupoDTO[]> {
        const response = await firstValueFrom(
            this.httpClient.get<IGrupoDTO[]>(
                `${Constants.apiURL}/researches/${investigacion.id}/groups`
            )
        );

        return response.map(grupo => new GrupoDTO(grupo));
    }

    public async postInvestigacionGrupos(investigacion: InvestigacionDTO, grupo: GrupoHeadDTO): Promise<GrupoDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IGrupoDTO>(
                `${Constants.apiURL}/researches/${investigacion.id}/groups`, grupo.obtenerJSON()
            )
        );

        return new GrupoDTO(response);
    }

    public async putInvestigacionGrupos(grupo: GrupoHeadDTO): Promise<GrupoDTO> {
        const response = await firstValueFrom(
            this.httpClient.put<IGrupoDTO>(
                `${Constants.apiURL}/campaigns/${grupo.id}`, grupo.obtenerJSON()
            )
        );

        return new GrupoDTO(response);
    }

    public async deleteInvestigacionGrupos(investigacion: InvestigacionDTO, grupo: GrupoHeadDTO): Promise<void> {
        return await firstValueFrom(
            this.httpClient.delete<void>(
                `${Constants.apiURL}/researches/${investigacion.id}/groups/${grupo.id}`, {}
            )
        );
    }

    public async getInvestigacionEnvios(investigacion: InvestigacionDTO): Promise<EnvioDTO[]> {
        const response = await firstValueFrom(
            this.httpClient.get<IEnvioDTO[]>(
                `${Constants.apiURL}/researches/${investigacion.id}/series`
            )
        );

        return response.map(envio => new EnvioDTO(envio));
    }

    public async postInvestigacionEnvio(envio: EnvioDTO): Promise<EnvioDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IEnvioDTO>(
                `${Constants.apiURL}/campaigns/${envio.grupoId}/series`, envio.toJSON()
            )
        );

        return new EnvioDTO(response);
    }

    public async putInvestigacionEnvio(envio: EnvioDTO): Promise<EnvioDTO> {
        const response = await firstValueFrom(
            this.httpClient.put<IEnvioDTO>(
                `${Constants.apiURL}/campaigns/${envio.grupoId}/series/${envio.id}`, envio.toJSON()
            )
        );

        return new EnvioDTO(response);
    }

    public async getInvestigacionEnvio(campaign_id: number, serie_id: number): Promise<EnvioDTO> {
        const response = await firstValueFrom(
            this.httpClient.get<IEnvioDTO>(
                `${Constants.apiURL}/campaigns/${campaign_id}/series/${serie_id}`
            )
        );

        return new EnvioDTO(response);
    }

    public async getInvestigacionRespuestas(
        investigacion: InvestigacionDTO, encuesta: EncuestaDTO, cantidadPreguntas: Nilable<number> = null,
        index: Nilable<number> = null
    ): Promise<RespuestaEncuesta[]> {
        const params: {[key: string]: string} = { combo: encuesta.id.toString() };

        if (cantidadPreguntas != null) {
            params.question_count = cantidadPreguntas.toString();
        }

        if (index != null) {
            params.index = index.toString();
        }

        const response = await firstValueFrom(
            this.httpClient.get<IRespuestaEncuesta[]>(
                `${Constants.apiURL}/researches/${investigacion.id}/answers`,
                { params }
            )
        );

        return response.map(respuestaEncuesta => new RespuestaEncuesta(respuestaEncuesta));
    }

    public getCuotas(investigacion: InvestigacionDTO): Promise<CuotaDTO[]> {
        return firstValueFrom(
            this.httpClient.get<ICuotaDTO[]>(`${Constants.apiURL}/researches/${investigacion.id}/universe`).pipe(
                map(cuotas => cuotas.map(cuota => new CuotaDTO(cuota)))
            )
        );
    }

    public postCuotas(investigacion: InvestigacionDTO, cuota: CuotaDTO): Observable<CuotaDTO> {
        return this.httpClient.post<ICuotaDTO>(
            `${Constants.apiURL}/researches/${investigacion.id}/universe`, cuota.toJSON()
        ).pipe(
            map(cuotaDTO => new CuotaDTO(cuotaDTO))
        );
    }

    public patchCuota(investigacion: InvestigacionDTO, cuota: CuotaDTO): Observable<CuotaDTO> {
        return this.httpClient.patch<ICuotaDTO>(
            `${Constants.apiURL}/researches/${investigacion.id}/universe/${cuota.id}`, cuota.toJSON()
        ).pipe(
            map(cuotaDTO => new CuotaDTO(cuotaDTO))
        );
    }

    public deleteCuota(investigacion: InvestigacionDTO, cuota: CuotaDTO): Observable<void> {
        return this.httpClient.delete<void>(`${Constants.apiURL}/researches/${investigacion.id}/universe/${cuota.id}`);
    }

    public async obtenerMarcadores(investigacion: InvestigacionDTO, envio: EnvioDTO): Promise<MarcadorDTO[]> {
        return await firstValueFrom(
            this.httpClient.get<IMarcadorDTO[]>(
                `${Constants.apiURL}/researches/${investigacion.id}/series/${envio.id}/bookmarks`
            ).pipe(
                map(marcadoresDTO => marcadoresDTO.map(marcadorDTO => new MarcadorDTO(marcadorDTO)))
            )
        );
    }

    public async agregarMarcador(
        investigacion: InvestigacionDTO, envio: EnvioDTO, marcador: MarcadorDTO
    ): Promise<MarcadorDTO> {
        return await firstValueFrom(
            this.httpClient.post<IMarcadorDTO>(
                `${Constants.apiURL}/researches/${investigacion.id}/series/${envio.id}/bookmarks`, marcador.toJSON()
            ).pipe(
                map(marcadorDTO => new MarcadorDTO(marcadorDTO))
            )
        );
    }

    public getSPSS(investigacion: InvestigacionDTO): Observable<Blob> {
        return this.httpClient.get(
            `${Constants.apiURL}/researches/${investigacion.id}/data/spss`, { responseType: "blob" }
        );
    }

    public async getFunnels(investigacion: InvestigacionDTO, envio: EnvioDTO): Promise<FunnelDTO[]> {
        return await firstValueFrom(
            this.httpClient.get<IFunnelDTO[]>(
                `${Constants.apiURL}/researches/${investigacion.id}/series/${envio.id}/funnels`
            ).pipe(
                map(funnels => funnels.map(funnel => new FunnelDTO(funnel)))
            )
        );
    }

    public postFunnel(investigacion: InvestigacionDTO, envio: EnvioDTO, funnel: FunnelDTO): Observable<FunnelDTO> {
        return this.httpClient.post<IFunnelDTO>(
            `${Constants.apiURL}/researches/${investigacion.id}/series/${envio.id}/funnels`, funnel.toJSON()
        ).pipe(
            map(funnelDTO => new FunnelDTO(funnelDTO))
        );
    }

    public deleteFunnel(investigacion: InvestigacionDTO, envio: EnvioDTO, funnel: FunnelDTO): Observable<void> {
        return this.httpClient.delete<void>(
            `${Constants.apiURL}/researches/${investigacion.id}/series/${envio.id}/funnels/${funnel.id}`
        );
    }

    public obtenerInfoItemCuestionario(
        grupoId: number, envioId: number, cuestionarioId: number
    ): Observable<EncuestaCheckpointDTO> {
        return this.httpClient.get<IEncuestaCheckpointDTO>(
            `${Constants.apiURL}/campaigns/${grupoId}/series/${envioId}/combos/${cuestionarioId}`
        ).pipe(
            map(response => new EncuestaCheckpointDTO(response))
        );
    }

    public async respuestasPorDia(investigacionId: number, envioId: number): Promise<RespuestaDiaDTO[]> {
        return await firstValueFrom(
            this.httpClient.get<IRespuestaDiaDTO[]>(
                `${Constants.apiURL}/researches/${investigacionId}/series/${envioId}/answers_by_day`
            ).pipe(
                map(response => response.map(item => new RespuestaDiaDTO(item)))
            )
        );
    }

    public getBitacoras(investigacionId: number): Observable<BitacoraDTO[]> {
        return this.httpClient.get<IBitacoraDTO[]>(
            `${Constants.apiURL}/researches/${investigacionId}/logs`
        ).pipe(
            map(bitacoras => bitacoras.map(bitacora => new BitacoraDTO(bitacora)))
        );
    }

    public postBitacora(investigacionId: number, bitacora: BitacoraDTO): Observable<BitacoraDTO> {
        return this.httpClient.post<IBitacoraDTO>(
            `${Constants.apiURL}/researches/${investigacionId}/logs`, bitacora.toJSON()
        ).pipe(
            map(bitacoraNueva => new BitacoraDTO(bitacoraNueva))
        );
    }

    public async postClonarInvestigacion(investigacionId: number, moverCuestionarios: boolean,
        clonarSeries: boolean, nombre: string): Promise<InvestigacionDTO> {
        const formData = new FormData();
        formData.append("clonarSeries", clonarSeries.toString());
        formData.append("moverCuestionarios", moverCuestionarios.toString());
        formData.append("investigacion", investigacionId.toString());
        formData.append("nombre", nombre);
        const response = await firstValueFrom(
            this.httpClient.post<IInvestigacionDTO>(`${Constants.apiURL}/researches/clone`, formData)
        );

        return new InvestigacionDTO(response);
    }
}
